<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-3xl">
      <div class="flex items-center space-x-4 mb-6">
        <button @click="goBack" class="p-2 hover:bg-gray-100 rounded-lg">
          <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h1 class="text-2xl font-bold text-gray-900">Credit Verification</h1>
      </div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 class="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">History</h2>
        <div v-if="verifications.length" class="space-y-3">
          <div
            v-for="v in verifications"
            :key="v.id"
            class="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3"
          >
            <div>
              <p class="text-sm text-gray-900">{{ v.summary || "No summary provided" }}</p>
              <p class="text-xs text-gray-500 mt-1">{{ formatDate(v.created_at) }} — {{ v.user?.name }}</p>
            </div>
            <StatusBadge :status="v.decision_label" />
          </div>
        </div>
        <p v-else class="text-gray-500 text-sm">No credit verification submitted yet.</p>
      </div>

      <div v-if="isAlreadyVerified" class="text-sm text-gray-500 italic px-4 py-3 text-center">
        Credit verification already submitted — decision cannot be changed here.
      </div>
      <button
        v-else
        @click="openModal"
        class="w-full flex items-center justify-center px-4 py-3 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700 transition-colors"
      >
        Submit Credit Verification
      </button>
    </div>

    <!-- Submit Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="closeModal"></div>
        <div class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-6 pt-5 pb-4 sm:p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Credit Verification Decision</h3>
            <form @submit.prevent="submit">
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Decision</label>
                <select v-model="form.decision" required class="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Select decision</option>
                  <option :value="1">Approve</option>
                  <option :value="0">Reject</option>
                </select>
              </div>
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Summary</label>
                <textarea v-model="form.summary" required rows="4" class="w-full border border-gray-300 rounded-lg px-3 py-2"></textarea>
              </div>
              <div class="mt-6 flex justify-end space-x-3">
                <button type="button" @click="closeModal" class="px-4 py-2 border border-red-400 text-red-400 rounded-lg">Cancel</button>
                <button type="submit" :disabled="isSubmitting" class="px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700">
                  {{ isSubmitting ? "Submitting..." : "Submit" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import StatusBadge from "@/components/ui/StatusBadge.vue";
import api from "@/utils/api";
import { useNotifications } from "@/composables/useNotifications";

const route = useRoute();
const router = useRouter();
const { showError, formatValidationErrors } = useNotifications();

const verifications = ref([]);
const showModal = ref(false);
const isSubmitting = ref(false);
const form = ref({ decision: "", summary: "" });

const isAlreadyVerified = computed(() => verifications.value.length > 0);

const goBack = () => router.push(`/admin/tickets/${route.params.id}`);

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

const fetchVerifications = async () => {
  try {
    const response = await api.get("/api/v2/credit-verifications", {
      params: { ticket_id: route.params.id },
    });
    verifications.value = response.data;
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load credit verifications", details, 5000);
  }
};

const openModal = () => {
  form.value = { decision: "", summary: "" };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const submit = async () => {
  isSubmitting.value = true;
  try {
    await api.post("/api/v2/credit-verifications", {
      ...form.value,
      ticket_id: route.params.id,
    });
    closeModal();
    fetchVerifications();
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to submit credit verification", details, 5000);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(fetchVerifications);
</script>